import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

// ── Panel styles ──────────────────────────────────────────────────────────────
export const panelStyles = [
  hostReset,
  css`
    :host {
      display: block;
      border:        var(--uwc-acc-panel-border,  1px solid ${t.border});
      border-radius: var(--uwc-acc-panel-radius,  ${t.radiusLg});
      overflow:      hidden;
      background:    var(--uwc-acc-panel-bg,      ${t.surface});
    }

    /* Grouped / stacked mode — set by parent uwc-accordion[stacked] */
    :host([grouped]) {
      border-radius: 0;
      margin-top:    -1px;
    }
    :host([grouped]:first-of-type) { border-radius: ${t.radiusLg} ${t.radiusLg} 0 0; margin-top: 0; }
    :host([grouped]:last-of-type)  { border-radius: 0 0 ${t.radiusLg} ${t.radiusLg}; }
    :host([grouped]:only-of-type)  { border-radius: ${t.radiusLg}; margin-top: 0; }

    /* ── <details> reset ───────────────────────────────────────────────── */
    details {
      display: block;
    }

    /* ── <summary> — reset browser defaults, apply design tokens ────────── */
    summary {
      display:        flex;
      list-style:     none;
      align-items:    center;
      gap:            ${t.space2};
      cursor:         pointer;
      user-select:    none;

      padding:     var(--uwc-acc-header-padding,      ${t.space4} ${t.space5});
      background:  var(--uwc-acc-header-bg,           transparent);
      color:       var(--uwc-acc-header-color,        ${t.text});
      font-size:   var(--uwc-acc-header-font-size,    ${t.fontSizeMd});
      font-weight: var(--uwc-acc-header-font-weight,  ${t.fontWeightSemibold});
      line-height: 1.4;

      transition: background ${t.durationBase} ease, color ${t.durationBase} ease;
    }

    summary::-webkit-details-marker { display: none; }

    summary:hover {
      background: var(--uwc-acc-header-hover-bg, ${t.hoverBg});
    }

    summary:focus-visible {
      outline:        2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
      border-radius:  ${t.radiusSm};
    }

    .uwc-acc-panel__header--disabled {
      cursor:         not-allowed;
      opacity:        0.48;
      pointer-events: none;
    }

    details[open] > summary {
      color:      var(--uwc-acc-header-active-color, ${t.primary});
      background: var(--uwc-acc-header-active-bg,    transparent);
    }

    /* ── Title ─────────────────────────────────────────────────────────── */
    .uwc-acc-panel__title {
      flex:          1;
      min-width:     0;
      overflow:      hidden;
      text-overflow: ellipsis;
      white-space:   nowrap;
    }

    /* ── Toggle icon wrapper ──────────────────────────────────────────────
       Rotates 90° when the panel is open so chevron-right naturally becomes
       chevron-down (pointing downward). The transition is independent of the
       WAAPI content animation so both run in parallel and finish together.
    ─────────────────────────────────────────────────────────────────────── */
    .uwc-acc-panel__chevron {
      flex-shrink:     0;
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      color:           var(--uwc-acc-icon-color, ${t.textSecondary});
      transform:       rotate(0deg);
      transition:      transform 250ms ease, color 250ms ease;
    }

    /* Default (end) position: push the icon to the trailing edge */
    :host(:not([toggle-icon-pos="start"])) .uwc-acc-panel__chevron {
      margin-left: auto;
    }

    /* Rotate 90° and change colour when open */
    details[open] > summary .uwc-acc-panel__chevron {
      transform: rotate(90deg);
      color:     var(--uwc-acc-icon-active-color, ${t.primary});
    }

    /* ── Content wrapper ──────────────────────────────────────────────────
       Height is animated by WAAPI in JavaScript (open: 0 → scrollHeight,
       close: scrollHeight → 0). overflow:hidden is applied inline during the
       animation and removed on finish so natural scrolling is restored.
       No grid-template-rows trick needed.
    ─────────────────────────────────────────────────────────────────────── */
    .uwc-acc-panel__content-wrap {
      /* Baseline — overflow is set/removed inline by WAAPI animation */
    }

    /* ── Content body ──────────────────────────────────────────────────── */
    .uwc-acc-panel__content {
      border-top:  1px solid var(--uwc-acc-content-border, ${t.borderSubtle});
      padding:     var(--uwc-acc-content-padding, 0 ${t.space5} ${t.space5});
      color:       var(--uwc-acc-content-color,   ${t.text});
      font-size:   var(--uwc-acc-content-font-size, ${t.fontSizeMd});
      line-height: 1.6;
    }

    /* ── Reduced motion ────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-acc-panel__chevron { transition: none; }
      summary                 { transition: none; }
      /* Content height animation duration is set to 0ms in JS via _duration() */
    }
  `,
];

// ── Accordion container styles ────────────────────────────────────────────────
export const accordionStyles = [
  hostReset,
  css`
    :host {
      display:        flex;
      flex-direction: column;
      gap:            var(--uwc-acc-gap, ${t.space2});
    }

    :host([stacked]) { gap: 0; }

    slot { display: contents; }
  `,
];
